<template>
  <DbCard
    v-if="messages.length"
    class="db-tw-p-5"
  >
    <template v-slot:content>
      <p>
        {{ $t('This message will') }} <b class="db-tw-font-bold">{{ $t('only be visible on preview website') }}</b>
        {{ $t('and warns you about possible configuration issues in your form.') }}
      </p>
      <div
        v-for="(type, index) in messageTypes"
        :key="index"
        class="db-tw-mt-4"
      >
        <div class="db-title-5">{{ type.title }} ({{ type.messages.length }})</div>
        <p class="db-text-small">
          {{ type.description }}
        </p>
        <ul class="db-tw-list-disc db-tw-pl-5">
          <li
            v-for="(error, i) in type.messages"
            :key="i"
          >
            {{ error.message }}
          </li>
        </ul>
      </div>
    </template>
  </DbCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { RfpFieldGroup } from '@/features/RfpForm/types/RfpFields'
import type { RfpFormStep } from '@/features/RfpForm/types/RfpFormSteps'
import { FieldGroupTypes } from '@/features/RfpForm/types/FieldGroupTypes'
import type { ConfigurationMessage } from './RfpConfigurationWarningTypes'
import {
  checkValidityEventTypes,
  checkValidityLocations,
  checkValidityBudget,
  checkValidityRoomSetup,
  checkValidityAddEvents,
} from './utils'
import type { RfpFormData } from '@/features/RfpForm/types/RfpFormData'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  /** Schema for the form, provided by the backoffice */
  stepsSchema: RfpFormStep[]
  defaultData: RfpFormData
}>()

const { t } = useI18n()

const messages = computed(() => {
  const allFields: RfpFieldGroup[] = []
  props.stepsSchema.map((step) => allFields.push(...step.rfp_fields))

  const allMessages: ConfigurationMessage[] = []

  // Empty form
  if (props.stepsSchema.length === 0) {
    allMessages.push({
      type: 'error',
      message: t('The form does not have any step'),
    })
  }

  // Empty Step
  props.stepsSchema.forEach((step, index) => {
    if (step.rfp_fields.length === 0) {
      allMessages.push({
        type: 'warning',
        message: t('Step {x} ({y}) is empty', {x: String(index + 1), y: step.name}),
      })
    }
  })

  const isSingleHotel = allFields.find((field) => field.field_id === FieldGroupTypes.LOCATIONS)
    ? false
    : true
  if (isSingleHotel && props.defaultData.selectedLocations.length === 0) {
    allMessages.push({
      type: 'error',
      message: t('Hotel associated to this form does not have a Meeting Broker ID'),
    })
  }

  // Invalid field
  allFields.forEach((field) => {
    switch (field.field_id) {
      case FieldGroupTypes.LOCATIONS:
        allMessages.push(...checkValidityLocations(field))
        break
      case FieldGroupTypes.EVENT_TYPE:
        allMessages.push(...checkValidityEventTypes(field))
        break
      case FieldGroupTypes.BUDGET:
        allMessages.push(...checkValidityBudget(field))
        break
      case FieldGroupTypes.ROOM_SETUP:
        allMessages.push(...checkValidityRoomSetup(field))
        break
      case FieldGroupTypes.ADD_EVENTS:
        allMessages.push(...checkValidityAddEvents(field))
        break
    }
  })

  return allMessages
})

const messageTypes = computed(() => {
  const errors = {
    title: 'Errors',
    description: t('Errors will prevent the user from submitting the form.'),
    messages: messages.value.filter((message) => message.type === 'error'),
  }

  const warnings = {
    title: 'Warnings',
    description: t('Warnings may cause unexpected design issues'),
    messages: messages.value.filter((message) => message.type === 'warning'),
  }

  return [
    ...(errors.messages.length ? [errors] : []),
    ...(warnings.messages.length ? [warnings] : []),
  ]
})
</script>
